import React from "react";
import styles from "./BreadCrumbs.module.scss";
import CustomLink from "@Components/CustomLink";
import urlJoin from "helpers/urljoin";
import config from "@Config/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import HelmetServer from "@Components/HelmetServer";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import useIsDesktop from "hooks/useIsDesktop";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import wrapperHOC from "@Utils/wrapperHOC";

interface IProps {
  data: Array<{
    link: string;
    name: string;
  }>;
  className?: string;
  itemClassName?: string;
  isPropertyPage?: boolean;
  dataTestId?: string;
  truncateLastItem?: boolean;
  hideLastItem?: boolean;
}
const BreadCrumbs = (props: IProps) => {
  const {
    data,
    className,
    itemClassName,
    isPropertyPage,
    dataTestId,
    truncateLastItem,
    hideLastItem,
  } = props;
  const isDesktop = useIsDesktop();

  const shouldHideLastItem = hideLastItem === true;

  const handleLinkOnClick = (item: any) => {
    gtagEventEmitter({
      action: "overview_navigation_clicked",
      is_property_page: isPropertyPage,
      link_url: item.link,
    });
  };

  const getBreadcrumbSchema = () => {
    // Always include all breadcrumbs in schema, even if hideLastItem is true
    const itemList = data.map((breadcrumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: urlJoin(config.WEB_SERVICE_URL, breadcrumb.link),
      name: breadcrumb.name,
    }));

    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: itemList,
    };
    return JSON.stringify(schema);
  };

  const nonce = useSelector(({ appData }: AppState) => appData.nonce, shallowEqual);

  const renderItem = (item: any, isLastItem: boolean) => {
    const isClickable = isLastItem && isPropertyPage;

    if (isClickable) {
      return (
        <CustomLink
          to={item.link}
          redirect={isDesktop}
          className={formatClassNames(itemClassName, styles.name)}
          dataTestId={dataTestId}
          onClick={() => handleLinkOnClick(item)}
        >
          {item.name}
        </CustomLink>
      );
    }

    if (isLastItem) {
      return (
        <span
          className={formatClassNames(itemClassName, styles.name, styles.lastItem)}
          data-testid={dataTestId}
        >
          {item.name}
        </span>
      );
    }

    return (
      <CustomLink
        to={item.link}
        redirect={isDesktop}
        className={formatClassNames(itemClassName, styles.name)}
        dataTestId={dataTestId}
        onClick={() => handleLinkOnClick(item)}
      >
        {item.name}
      </CustomLink>
    );
  };

  return (
    <ul
      className={formatClassNames(
        formatClassNames(
          styles.container,
          className,
          isDesktop && styles.desktop,
          isPropertyPage && styles.propertyPage,
          shouldHideLastItem && styles.hideLastItem,
        ),
      )}
    >
      <HelmetServer>
        <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
          {getBreadcrumbSchema()}
        </script>
      </HelmetServer>
      {data.map((item, index) => {
        const isLastItem = index === data.length - 1;

        return (
          <React.Fragment key={index}>
            <li className={formatClassNames(truncateLastItem && isLastItem && styles.truncate)}>
              {renderItem(item, isLastItem)}
            </li>
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export default wrapperHOC(BreadCrumbs, {
  componentName: "BreadCrumbs-Global",
  showForChina: true,
  showForMiniProgram: false,
  showForWhiteLable: false,
  showForApp: false,
});
